import {
    AudioSearchFilterContentTypes,
    ImageSearchFilterContentTypes,
    VideoSearchFilterContentTypes,
} from 'search-gateway-client/components';

/**
 * Single source of truth for the copy shown when conversational search is unavailable for the
 * current content type. It is surfaced in three places — the toggle's `aria-label`, the dedicated
 * CTA button's label, and the tooltip body — which must never drift apart.
 *
 * The supported set mirrors `isSearchWithAIEnabledForContentType` in `useSearchWithAI`: the video
 * family, plus audio and images when their flags are on. The message must be derived from those
 * same flags rather than hardcoded, otherwise it claims a content type is unsupported while the
 * gate actually permits it.
 */
export function getConversationalSearchUnavailableMessage(
    showConversationalSearchAudio: boolean,
    showConversationalSearchImages: boolean = false,
): string {
    const supportedContent = [
        'video',
        ...(showConversationalSearchAudio ? ['audio'] : []),
        ...(showConversationalSearchImages ? ['image'] : []),
    ];
    return `Conversational Search is only available for ${formatList(supportedContent)} content.`;
}

function formatList(items: readonly string[]): string {
    if (items.length <= 2) return items.join(' and ');
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

type ContentFamily = 'video' | 'audio' | 'image';

export function getContentFamily(contentType: string | undefined): ContentFamily | undefined {
    if (contentType === undefined) return undefined;
    if (Object.values<string>(VideoSearchFilterContentTypes).includes(contentType)) return 'video';
    if (Object.values<string>(AudioSearchFilterContentTypes).includes(contentType)) return 'audio';
    if (Object.values<string>(ImageSearchFilterContentTypes).includes(contentType)) return 'image';
    return undefined;
}

/** Falls back to video copy because video is the only family conversational search always supports. */
export function getConversationalSearchHeading(contentType: string | undefined): string {
    return `Find the perfect ${getContentFamily(contentType) ?? 'video'} with AI Search`;
}
